// src/hooks/useClientForm.js
import { useState, useCallback } from "react";
import { clientService } from "../api/clientService";

const INITIAL = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  clientType: "",
  leadSource: "",
  stage: "New Lead",
  notes: "",
  birthday: "",
  anniversary: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS = (v) => (v.match(/\d/g) || []).length;

function validate(form) {
  const errs = {};
  if (!form.firstName.trim()) errs.firstName = "First name is required";
  else if (form.firstName.trim().length > 100) errs.firstName = "Max 100 characters";
  if (!form.lastName.trim()) errs.lastName = "Last name is required";
  else if (form.lastName.trim().length > 100) errs.lastName = "Max 100 characters";
  if (form.email && !EMAIL_RE.test(form.email)) errs.email = "Please enter a valid email";
  if (form.phone) {
    const d = PHONE_DIGITS(form.phone);
    if (d < 7 || d > 15) errs.phone = "Please enter a valid phone number (7-15 digits)";
  }
  if (form.notes && form.notes.length > 2000) errs.notes = "Notes cannot exceed 2000 characters";
  if (form.leadSource && form.leadSource.length > 100) errs.leadSource = "Lead source cannot exceed 100 characters";
  return errs;
}

function clientToForm(client) {
  if (!client) return INITIAL;
  return {
    firstName: client.first_name || "",
    lastName: client.last_name || "",
    email: client.email || "",
    phone: client.phone || "",
    clientType: client.client_type || "",
    leadSource: client.lead_source || "",
    stage: client.stage || "New Lead",
    notes: client.notes || "",
    birthday: client.birthday || "",
    anniversary: client.anniversary || "",
  };
}

export function useClientForm({ mode, initialValues, onSuccess, onClose }) {
  const [form, setForm] = useState(() => clientToForm(initialValues));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear field error on change
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setServerError("");
  }, []);

  const handleBlur = useCallback((field) => () => {
    const fieldErrors = validate(form);
    if (fieldErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
    }
  }, [form]);

  const reset = useCallback(() => {
    setForm(clientToForm(initialValues));
    setErrors({});
    setServerError("");
  }, [initialValues]);

  const handleSubmit = useCallback(async () => {
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    setServerError("");
    try {
      if (mode === "create") {
        await clientService.create(form);
      } else {
        await clientService.update(initialValues.id, form);
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      if (err.status === 409) {
        setServerError(err.message);
      } else {
        setServerError(err.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  }, [form, mode, initialValues, onSuccess, onClose]);

  return { form, errors, serverError, loading, handleChange, handleBlur, handleSubmit, reset };
}
